import { useEffect, useState } from 'react';
import { searchCities, computeNatal, type City, type NatalChart } from '../api';

interface Props {
  onComputed: (chart: NatalChart) => void;
}

export function BirthForm({ onComputed }: Props) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [timeUnknown, setTimeUnknown] = useState(false);
  const [query, setQuery] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [selected, setSelected] = useState<City | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (selected) return;
    if (query.trim().length < 2) {
      setCities([]);
      return;
    }
    let cancelled = false;
    searchCities(query.trim())
      .then((r) => {
        if (!cancelled) setCities(r.cities);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [query, selected]);

  // Button is active as soon as date and city query are present. If the user
  // typed a city without picking a dropdown suggestion, we resolve the top
  // match at submit time — friendlier than silently disabling the button.
  const canSubmit = !!date && (!!selected || query.trim().length >= 2) && (timeUnknown || !!time);

  async function submit() {
    if (!canSubmit) return;
    setLoading(true);
    setErr(null);
    try {
      let city = selected;
      if (!city) {
        const r = await searchCities(query.trim());
        city = r.cities[0] ?? null;
        if (!city) {
          setErr('Nie znalazłem tego miasta / City not found');
          return;
        }
        setSelected(city);
      }
      const res = await computeNatal({
        birthDate: date,
        birthTime: timeUnknown ? null : time,
        latitude: city.lat,
        longitude: city.lng,
        timezone: city.tz,
        cityName: `${city.name}, ${city.country}`,
      });
      onComputed(res.chart);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card">
      <label>
        <div className="muted">Data urodzenia / Birth date</div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} max="2099-12-31" />
      </label>

      <label>
        <div className="muted">Godzina / Birth time</div>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={timeUnknown}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={timeUnknown}
            onChange={(e) => setTimeUnknown(e.target.checked)}
          />
          <span className="muted">Nie znam godziny / Don't know time (solar chart)</span>
        </label>
      </label>

      <label>
        <div className="muted">Miasto / Birth city</div>
        <input
          type="text"
          placeholder="Warszawa, Kraków, London, …"
          value={selected ? `${selected.name}, ${selected.country}` : query}
          onChange={(e) => {
            setSelected(null);
            setQuery(e.target.value);
          }}
        />
      </label>

      {cities.length > 0 && !selected && (
        <div>
          {cities.map((c) => (
            <div
              key={`${c.name}-${c.country}-${c.lat}`}
              className="city-suggestion"
              onClick={() => {
                setSelected(c);
                setCities([]);
              }}
            >
              {c.name}, {c.country} · {c.tz}
            </div>
          ))}
        </div>
      )}

      {err && <div className="muted" style={{ color: 'var(--aspect-chal)' }}>{err}</div>}

      <button className="btn" disabled={!canSubmit || loading} onClick={submit}>
        {loading ? '…' : 'Policz kartę / Compute chart'}
      </button>
    </div>
  );
}
